import * as Sentry from "@sentry/tanstackstart-react";
import { isNotFound, isRedirect } from "@tanstack/react-router";
import { isbot } from "isbot";
import { z } from "zod/v4";
import { ApiError } from "#/modules/util.auth/api-errors";
import { isConnectionLost } from "#/modules/util.errors/middleware.server-unavailable";
import { ApiFailureError, isKnownErrorTitle } from "#/modules/util.errors/translate";

// A known code that can only mean the frontend sent a broken request.
const FRONTEND_BUG_TITLES = new Set(["MissingTenantHeader"]);

const STALE_CHUNK =
  /dynamically imported module|Importing a module script failed|Unable to preload/i;

// @simplewebauthn throws this plain Error when `navigator.credentials.get()` resolves `null`
// instead of rejecting, which is what Edge/Windows Hello does when the prompt is dismissed.
const NULL_CREDENTIAL_MESSAGE = "Authentication was not completed";

// Microsoft's Outlook safe-links scanner rejects with this string while crawling a mailed link.
const OUTLOOK_SCANNER = /^Object Not Found Matching Id:\d+/;

/** A lazy chunk from a previous build that is gone after a deploy. */
export function isStaleChunkError(cause: unknown) {
  return cause instanceof Error && STALE_CHUNK.test(cause.message);
}

// The browser has no working WebAuthn service (ChromeOS, locked-down devices); @simplewebauthn
// rethrows it as a plain Error whose message starts with the DOMException name.
const WEBAUTHN_NOT_SUPPORTED = /^NotSupportedError\b/;

// @simplewebauthn refuses the autofill offer when no input has `webauthn` last in `autocomplete`
// at that moment. The login email field always has it, so something on the device (e.g. a
// password manager) rewrote the attribute; the passkey button still works there.
const WEBAUTHN_AUTOFILL_INPUT_MISSING = /^No <input> with "webauthn"/;

export function isWebAuthnUnavailable(cause: unknown) {
  if (!(cause instanceof Error || cause instanceof DOMException)) return false;
  return (
    cause.name === "NotSupportedError" ||
    WEBAUTHN_NOT_SUPPORTED.test(cause.message) ||
    WEBAUTHN_AUTOFILL_INPUT_MISSING.test(cause.message)
  );
}

// Chromium's wording when the page's host is outside the passkey RP ID; @simplewebauthn keeps it.
const FOREIGN_RP_ID = /^The RP ID "([^"]+)" is invalid for this domain/;

/**
 * The login page loaded on a host the passkey RP ID does not cover, e.g. a crawler on
 * maintenance.beweegbaas.nl. On the RP ID's own host the same error is a real misconfiguration.
 */
export function isPasskeyOnForeignHost(cause: unknown) {
  if (import.meta.env.SSR) return false;
  if (!(cause instanceof Error || cause instanceof DOMException)) return false;
  if (cause.name !== "SecurityError") return false;
  const rpId = FOREIGN_RP_ID.exec(cause.message)?.[1];
  if (!rpId) return false;
  const host = window.location.hostname;
  return host !== rpId && !host.endsWith(`.${rpId}`);
}

// A search engine's renderer (bingbot, Googlebot, …) refuses part of what a page requests (a
// synthetic 403 for a server fn, a lazy chunk it will not fetch), so what breaks there is its own
// doing. A bug in our code also hits real users, whose browsers report it.
export function isCrawler() {
  return !import.meta.env.SSR && isbot(navigator.userAgent);
}

// The browser or OS will not start audio output (old iPadOS, busy device).
const AUDIO_DEVICE = /audio device/i;

export function isAudioDeviceUnavailable(cause: unknown) {
  return (
    cause instanceof DOMException &&
    cause.name === "InvalidStateError" &&
    AUDIO_DEVICE.test(cause.message)
  );
}

// The device already holds a passkey for this account (WebAuthn excludeCredentials): a user
// action with its own message, not a fault.
const ALREADY_REGISTERED = /previously registered|already registered/i;

export function isPasskeyAlreadyRegistered(cause: unknown) {
  if (!(cause instanceof Error || cause instanceof DOMException)) return false;
  return cause.name === "InvalidStateError" && ALREADY_REGISTERED.test(cause.message);
}

export function isWebAuthnCancellation(cause: unknown) {
  // Not every runtime makes DOMException an Error subclass.
  if (!(cause instanceof Error || cause instanceof DOMException)) return false;
  return (
    cause.name === "NotAllowedError" ||
    cause.name === "AbortError" ||
    cause.message === NULL_CREDENTIAL_MESSAGE
  );
}

/**
 * A 4xx the user gets a translated message for: a documented code, or a 404 (something another
 * tab or user removed; `translateApiError` says "niet gevonden").
 */
export function isExpectedApiError(cause: unknown): cause is ApiError {
  if (!ApiError.is(cause) || cause.status < 400 || cause.status >= 500) return false;
  if (cause.status === 404) return true;
  return isKnownErrorTitle(cause.title) && !FRONTEND_BUG_TITLES.has(cause.title);
}

export type ExpectedErrorKind =
  | "redirect"
  | "not-found"
  | "response"
  | "api-failure"
  | "api-error"
  | "connection-lost"
  | "crawler"
  | "cancelled"
  | "stale-chunk"
  | "audio-unavailable"
  | "passkey-already-registered"
  | "webauthn-unavailable"
  | "passkey-foreign-host"
  | "outlook-scanner";

/**
 * The single decision on what reaches GlitchTip as an error: anything that is not a bug on our side
 * (control flow, a domain rule the user ran into, their network, their own cancel, a tab on an old
 * build, a crawler's renderer) gets a kind and becomes a log line instead (`logExpectedError`). Used
 * by the browser and server `beforeSend`, so no call site has to filter on its own.
 */
export function expectedErrorKind(cause: unknown): ExpectedErrorKind | undefined {
  if (isCrawler()) return "crawler";
  const rejectedWith = z.string().safeParse(cause);
  if (rejectedWith.success) {
    return OUTLOOK_SCANNER.test(rejectedWith.data) ? "outlook-scanner" : undefined;
  }
  // A router redirect is a Response too, so it has to be recognised first.
  if (isRedirect(cause)) return "redirect";
  if (isNotFound(cause)) return "not-found";
  if (cause instanceof Response) return "response";
  if (ApiFailureError.is(cause)) return "api-failure";
  if (isExpectedApiError(cause)) return "api-error";
  if (isConnectionLost(cause)) return "connection-lost";
  if (isWebAuthnCancellation(cause)) return "cancelled";
  if (isStaleChunkError(cause)) return "stale-chunk";
  if (isAudioDeviceUnavailable(cause)) return "audio-unavailable";
  if (isPasskeyAlreadyRegistered(cause)) return "passkey-already-registered";
  if (isWebAuthnUnavailable(cause)) return "webauthn-unavailable";
  if (isPasskeyOnForeignHost(cause)) return "passkey-foreign-host";
  return undefined;
}

type ExpectedErrorLog = {
  "error.kind": ExpectedErrorKind;
  "error.type": string;
  "error.message": string;
  "api.error.title"?: string;
  "api.error.status"?: number;
  flow?: string;
  "url.full"?: string;
};

/**
 * Kept out of GlitchTip issues, but still a log line, so a user's "it didn't work" can be traced
 * (e.g. search "expected error: connection-lost" for their email).
 */
export function logExpectedError(kind: ExpectedErrorKind, cause: unknown, flow?: string) {
  const error = z
    .object({ name: z.string().catch(""), message: z.string().catch("") })
    .catch({ name: "", message: z.string().catch("").parse(cause) })
    .parse(cause);
  const attributes: ExpectedErrorLog = {
    "error.kind": kind,
    "error.type": error.name,
    "error.message": error.message,
  };
  if (ApiError.is(cause)) {
    attributes["api.error.title"] = cause.title;
    attributes["api.error.status"] = cause.status;
  }
  if (flow) attributes.flow = flow;
  if (!import.meta.env.SSR) attributes["url.full"] = window.location.href;
  Sentry.logger.info(`expected error: ${kind}`, attributes);
}
